/**
 * @fileoverview Safe localStorage wrapper with JSON support
 * @module utils/localStorage
 */

/**
 * Get item from localStorage with JSON parsing
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Parsed value or default
 *
 * @example
 * getItem('user', null) // { name: 'John' }
 * getItem('token', '') // 'abc123'
 */
export const getItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;

    // Try to parse as JSON
    try {
      return JSON.parse(item);
    } catch {
      // Return as string if not valid JSON
      return item;
    }
  } catch (error) {
    console.error(`Error reading localStorage key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Set item in localStorage with JSON stringification
 * @param {string} key - Storage key
 * @param {*} value - Value to store
 * @returns {boolean} Success status
 *
 * @example
 * setItem('user', { name: 'John' })
 * setItem('token', 'abc123')
 */
export const setItem = (key, value) => {
  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
    return true;
  } catch (error) {
    console.error(`Error setting localStorage key "${key}":`, error);
    return false;
  }
};

/**
 * Remove item from localStorage
 * @param {string} key - Storage key
 * @returns {boolean} Success status
 */
export const removeItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error);
    return false;
  }
};

/**
 * Clear all localStorage
 * @returns {boolean} Success status
 */
export const clear = () => {
  try {
    const lang = localStorage.getItem('lang');
    localStorage.clear();
    if (lang) localStorage.setItem('lang', lang);
    return true;
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    return false;
  }
};

/**
 * Check if localStorage is available
 * @returns {boolean} Availability status
 */
export const isAvailable = () => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get all keys from localStorage
 * @returns {string[]} Array of keys
 */
export const getAllKeys = () => {
  try {
    return Object.keys(localStorage);
  } catch (error) {
    console.error('Error getting localStorage keys:', error);
    return [];
  }
};

/**
 * อ่านค่าที่ต้องเป็น object (profile/company) - ค่าเสียจนไม่ใช่ object (เช่น "{", "undefined") ถือว่าไม่มีข้อมูล
 * @param {string} key - Storage key
 * @returns {Object|null}
 */
export const getObjectItem = (key) => {
  const value = getItem(key, null);
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
};

/**
 * Get current user profile from localStorage
 * @returns {Object|null} User profile or null
 */
export const getProfile = () => {
  return getObjectItem('cm-profile');
};

/**
 * Get authentication token from localStorage
 * @returns {string} Token string
 */
export const getTokens = () => {
  return getItem('cm-acc-tk', '');
};

/**
 * Get current company from localStorage — อ่านค่าสดทุกครั้งที่เรียก (ไม่ cache)
 * ต่างจาก `company` ที่ export จาก CustomComponent.js ซึ่งอ่านครั้งเดียวตอนโหลดโมดูล
 * และจะไม่เห็นการเปลี่ยน/หายของ company ระหว่างใช้งานหน้า (เช่น session หมดอายุ, สลับบริษัท)
 * @returns {Object|null} Company object or null
 */
export const getCurrentCompany = () => {
  return getObjectItem('cm-company');
};

const localStorageUtils = {
  getItem,
  setItem,
  removeItem,
  clear,
  isAvailable,
  getAllKeys,
  getObjectItem,
  getProfile,
  getTokens,
  getCurrentCompany,
};

export default localStorageUtils;
